import type { ActionableResult, ActionExecutionContext } from './ActionRegistry';

/**
 * Modal operational mode — controls footer buttons AND propagates to form engine.
 *
 * - `'view'`        → Footer shows only Cancel (or Cancel + Edit). Form renders disabled.
 * - `'edit'`        → Footer shows Cancel + Save. Form renders editable.
 * - `'create'`      → Footer shows Cancel + Add. Form renders in create mode.
 * - `'add'`         → @deprecated alias for `'create'`.
 * - `'cancel-only'` → Footer shows only Cancel. Used for read-only informational modals.
 * - `'approver'`    → Footer shows Reject + Approve (two-action layout).
 */
export type ComposeModalMode = 'cancel-only' | 'view' | 'edit' | 'create' | 'approver' | 'add';

const ACTION_OBJECTS_KEY = '__WEALTHLANE_ACTION_OBJECTS__';

function getActionObjects(): Map<string, Action> {
  const w = window as any;
  if (!w[ACTION_OBJECTS_KEY]) {
    w[ACTION_OBJECTS_KEY] = new Map<string, Action>();
  }
  return w[ACTION_OBJECTS_KEY] as Map<string, Action>;
}

/**
 * The core contract for every action in the system.
 *
 * Each action is a self-contained strategy object that knows its identity,
 * its field requirements, its permission gate, its row-level guard, and
 * how to execute itself. The table is dumb — it delegates everything to
 * the Action object via the ActionRegistry.
 *
 * Naming convention: the `id` MUST follow VERB_DOMAIN or VERB_MODIFIER_DOMAIN
 * where the LAST word is always the domain name (e.g. 'participant', 'award').
 * This convention enables auto-loading of domain modules by the registry.
 */
export type ActionResult = void | Record<string, unknown>;

export interface Action {
  /** Semantic identity. Last word = domain. e.g. 'EDIT_PARTICIPANT', 'VIEW_AWARD'. */
  readonly id: string;

  /** Fields this action reads from the row. Declares the data contract. */
  readonly requires: readonly string[];

  /**
   * Global gate — is this action available at all?
   * No parameters. Reads permissions lazily from ambient singletons (Redux store).
   * Returns false → the action button is completely hidden for ALL rows.
   */
  isAvailable(): boolean;

  /**
   * Row-level gate — can this specific row have this action?
   * Returns visibility + enabled state per row.
   */
  isActionable(row: Record<string, unknown>): ActionableResult;

  /**
   * Execute the action. Self-contained.
   * - For row/bulk actions: call API, show toast, emit intent. Return void.
   * - For data actions: fetch and return data for the view engine.
   */
  execute(ctx: ActionExecutionContext): ActionResult | Promise<ActionResult>;
}

/**
 * Base class providing default implementations so concrete actions only
 * override what they need.
 *
 * Decorate subclasses with `@action` to auto-register them.
 */
export abstract class AbstractAction implements Action {
  abstract readonly id: string;
  readonly requires: readonly string[] = [];

  isAvailable(): boolean {
    return true;
  }

  isActionable(_row: Record<string, unknown>): ActionableResult {
    return { visible: true, enabled: true };
  }

  abstract execute(ctx: ActionExecutionContext): ActionResult | Promise<ActionResult>;
}

/**
 * Class decorator that auto-registers an AbstractAction subclass.
 *
 * Usage:
 * ```ts
 * @action
 * class ViewParticipantAction extends AbstractAction {
 *   readonly id = 'VIEW_PARTICIPANT';
 *   // ...
 * }
 * ```
 */
export function action<T extends new (...args: any[]) => AbstractAction>(
  Class: T
): T {
  const instance = new Class();
  const objects = getActionObjects();
  if (!objects.has(instance.id)) {
    objects.set(instance.id, instance);
  }
  return Class;
}

/**
 * A footer action button rendered by ComposeModal.
 * Maps to ModalSheet's onAction / onSecondaryAction slots based on position
 * and mode:
 *
 *   mode='view':     actions[0] → Edit button (onAction)
 *   mode='approver': actions[0] → Approve (onAction), actions[1] → Reject (onSecondaryAction)
 *
 * Each action emits an OPEN_MODAL intent with its `id` as the actionId.
 *
 * `intent` maps to ModalActionIntent ('edit' | 'approve' | 'reject' | 'delete' | etc.)
 * and auto-resolves the button's label, icon, variant, and type. Pass `label`
 * to override the auto-resolved text.
 */
export interface ComposeModalAction {
  id: string;
  intent: 'save' | 'edit' | 'add' | 'approve' | 'reject' | 'delete' | 'submit' | 'confirm';
  label?: string;
}

/**
 * Config for the auto-rendered compose modal (OPEN_COMPOSE_MODAL intent).
 * Lives directly in `context.meta`. ActionIntentHandler reads it to render
 * ModalSheet + ControlRenderer without a dedicated .tsx modal file.
 *
 * Actions array maps to footer buttons based on mode:
 * - No actions → mode defaults to 'cancel-only'
 * - 1 action   → mode defaults to 'view' (Cancel + primary action)
 * - 2 actions  → mode defaults to 'approver' (secondary + primary)
 * - Explicit mode overrides the auto-detection
 */
export interface ComposeModalConfig {
  title: string;
  subtitle?: string;
  /** Entity name (e.g. 'Participant', 'RSU Award'). When provided, the modal
   * title is derived as `${Mode} ${entity}` and updates automatically on
   * in-place mode switches. Falls back to static `title` if omitted. */
  entity?: string;
  size?: string;
  semanticId?: string;
  /** Operational mode controlling modal footer and form behavior. */
  mode?: ComposeModalMode;
  /** @deprecated Use `mode` instead. */
  footerMode?: ComposeModalMode;
  /** Override the primary action button label (e.g. 'Save' instead of the intent default 'Add'). */
  actionLabel?: string;
  actions?: ComposeModalAction[];
  /** After successful nested-form submit, refresh parent form async-select dropdowns. */
  asyncSelectActionIds?: string[];
  /**
   * Wealthlane form schema id (`wealthlane://forms/...`) for compose session side-store binding.
   * Required when the form uses registered side stores (e.g. plan termination rules).
   */
  formSchemaId?: string;
  /** Action ID to run when the modal finishes closing (after exit animation). */
  onExitCompleteActionId?: string;
  /** Action ID when the modal closes without a successful nested form submit (cancel / overlay). */
  onDismissActionId?: string;
  /** Data action invoked after a nested form submit succeeds (before the modal closes). */
  onSubmitSuccessActionId?: string;
}

/**
 * Panel chrome for OPEN_COMPOSE_POPOVER (AppShell ComposePopoverHandler).
 * Stored on `context.meta.composePopover`.
 */
export interface ComposePopoverConfig {
  panelClassName?: string;
  /** CSS width for popover body (default 40vw) */
  width?: string;
  maxWidthPx?: number;
}

/**
 * Typed intent emitted by actions that need to open a modal.
 *
 * Intent types:
 * - `OPEN_MODAL`: lazy-loads a dedicated modal component via modalLoaders map
 * - `OPEN_COMPOSE_MODAL`: auto-renders ModalSheet + ControlRenderer — actionId
 *   is the builder (e.g. BUILD_PARTICIPANT_VIEW), config lives in context.meta.
 * - `OPEN_COMPOSE_POPOVER`: mouse-anchored popover + ControlRenderer (schema modal columns)
 */
export type ActionIntent =
  | { type: 'OPEN_MODAL'; actionId: string; context?: ActionExecutionContext; payload?: Record<string, unknown> }
  | { type: 'OPEN_COMPOSE_MODAL'; actionId: string; context: ActionExecutionContext; loader?: () => Promise<{ default: any }> }
  | { type: 'OPEN_COMPOSE_POPOVER'; actionId: string; context: ActionExecutionContext };

/**
 * Shared props contract for every modal rendered by ActionIntentHandler.
 * Modal components implement this instead of ad-hoc prop interfaces.
 */
export interface ModalIntentProps {
  context: ActionExecutionContext;
  isOpen: boolean;
  onClose: () => void;
  onExitComplete?: () => void;
}
